import type { FC } from 'react';
import { Col, Form, type FormInstance, Row } from 'antd';

import FormInput from '@/views/components/Inputs/FormInput';
import FormTextArea from '@/views/components/Inputs/FormTextArea';
import HiddenFormInput from '@/views/components/Inputs/HiddenFormInput';
import PostalCodeInput, { type PostalCodeInputValues } from '@/views/components/Selectors/PostalCodeInput';
import { CitySelect, CountrySelect, StateSelect } from '@/views/screens/common';

import type { CarrierInformationSettings } from '../types';

interface CarrierClaimsAddressFormProps {
  addressType: 'ldClaimAddress' | 'ocClaimAddress';
  form: FormInstance<CarrierInformationSettings>;
}

const CarrierClaimsAddressForm: FC<CarrierClaimsAddressFormProps> = ({ addressType, form }) => {
  const countryCode = Form.useWatch([addressType, 'carrierDetailCountry'], form);
  const stateCode = Form.useWatch([addressType, 'carrierDetailState'], form);

  const handleZipDetails = (data: PostalCodeInputValues) => {
    form.setFieldValue([addressType, 'carrierDetailCity'], data?.city);
    form.setFieldValue([addressType, 'carrierDetailState'], data?.state);
    form.setFieldValue([addressType, 'carrierDetailZip'], data?.zip);
    form.setFieldValue([addressType, 'carrierDetailCountry'], data?.countryCode);
  };

  return (
    <Row gutter={20}>
      <Col md={12}>
        <FormInput
          editMode
          name={[addressType, 'carrierDetailName']}
          label="Name"
          margin="mb-15"
          placeholder="Enter name"
        />
        <FormTextArea
          editMode
          // @ts-expect-error
          name={[addressType, 'carrierDetailAddr1']}
          label="Address"
          margin="mb-15"
          rows={3}
          placeholder="Enter address"
        />
        <CountrySelect label="Country" editMode name={[addressType, 'carrierDetailCountry']} margin="mb-15" />
        <PostalCodeInput
          editMode
          countryCode={countryCode}
          // @ts-expect-error - name prop accepts array for nested form fields
          name={[addressType, 'carrierDetailZip']}
          label="Postal"
          placeholder="Enter postal code"
          margin="mb-15"
          handleZipDetails={handleZipDetails}
        />
      </Col>
      <Col md={12}>
        <CitySelect
          editMode
          name={[addressType, 'carrierDetailCity']}
          margin="mb-15"
          label="City"
          countryCode={countryCode}
          stateCode={stateCode}
        />
        <StateSelect
          editMode
          name={[addressType, 'carrierDetailState']}
          margin="mb-15"
          label="State"
          countryCode={countryCode}
        />
        <FormInput
          editMode
          name={[addressType, 'carrierDetailPhone']}
          label="Phone"
          margin="mb-15"
          inputType="text"
          placeholder="Enter phone number"
        />
        <FormInput
          editMode
          name={[addressType, 'carrierDetailFax']}
          label="Fax"
          margin="mb-15"
          inputType="text"
          placeholder="Enter 10-digit fax number"
          validateTrigger={['onChange', 'onBlur']}
          rules={[
            {
              validator: (_, value) => {
                if (!value) {
                  return Promise.resolve();
                }
                if (!/^\d{10}$/.test(value)) {
                  return Promise.reject(new Error('Fax must be exactly 10 digits'));
                }
                return Promise.resolve();
              },
            },
          ]}
        />
        <FormInput
          editMode
          name={[addressType, 'carrierEmail']}
          label="Email"
          margin="mb-0"
          placeholder="Enter email address"
          validateTrigger={['onChange', 'onBlur']}
          rules={[
            {
              message: 'The input is not valid E-mail!',
              type: 'email',
            },
          ]}
        />
      </Col>

      <HiddenFormInput name={[addressType, 'addressType']} />
      <HiddenFormInput name={[addressType, 'carrierDetailAddr2']} />
      <HiddenFormInput name={[addressType, 'carrierDetailKey']} />
      <HiddenFormInput name={[addressType, 'carrierKey']} />
      <HiddenFormInput name={[addressType, 'dirtyFlag']} />
    </Row>
  );
};

export default CarrierClaimsAddressForm;
